// src/pages/MyWork/MyWork.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckSquare, Clock, AlertCircle } from 'lucide-react';
import { Box, Typography } from '@mui/material';
import { useAppSelector } from '../../hooks/useAppSelector';
import { tasksApi } from '../../api/endpoints/tasksApi';
import { Task, TaskStatus } from '../../types/common';
import { StatCardSkeleton, ListRowSkeleton } from '../../components/common/Skeletons';
import './MyWork.scss';

const MyWork: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMyTasks = async () => {
      if (user?.id) {
        try {
          const response = await tasksApi.getUserTasks(user.id.toString());
          setTasks(response.tasks);
        } catch (error) {
          console.error('Failed to fetch user tasks:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchMyTasks();
  }, [user]);

  const handleTaskClick = (task: Task) => {
    if (task.boardId) {
      navigate(`/boards/${task.boardId}?taskId=${task.id}`);
    }
  };

  if (loading) {
    return (
      <div className="my-work-page">
        <div className="page-header">
          <h1>My Work</h1>
          <p>Track and manage your assigned tasks</p>
        </div>
        <div className="work-stats">
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
        </div>
        <div className="tasks-section">
          <h2>Your Tasks</h2>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <ListRowSkeleton key={i} height={72} />
            ))}
          </Box>
        </div>
      </div>
    );
  }

  return (
    <div className="my-work-page">
      <div className="page-header">
        <h1>My Work</h1>
        <p>Track and manage your assigned tasks</p>
      </div>

      <div className="work-stats">
        <div className="stat-card stagger-item">
          <CheckSquare className="stat-icon" />
          <div className="stat-content">
            <h3>Total Tasks</h3>
            <p className="stat-value">{tasks.length}</p>
          </div>
        </div>
        <div className="stat-card stagger-item" style={{ animationDelay: '60ms' }}>
          <Clock className="stat-icon" />
          <div className="stat-content">
            <h3>In Progress</h3>
            <p className="stat-value">{tasks.filter(t => t.status === TaskStatus.IN_PROGRESS).length}</p>
          </div>
        </div>
        <div className="stat-card stagger-item" style={{ animationDelay: '120ms' }}>
          <AlertCircle className="stat-icon" />
          <div className="stat-content">
            <h3>High Priority</h3>
            <p className="stat-value">{tasks.filter(t => t.priority?.toLowerCase() === 'high' || t.priority?.toLowerCase() === 'urgent').length}</p>
          </div>
        </div>
      </div>

      <div className="tasks-section">
        <h2>Your Tasks</h2>
        <div className="tasks-list">
          {tasks.length === 0 ? (
            <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
              You don't have any tasks assigned yet.
            </Typography>
          ) : (
            tasks.map((task, i) => (
              <div
                key={task.id}
                className="task-card stagger-item"
                onClick={() => handleTaskClick(task)}
                style={{ cursor: 'pointer', animationDelay: `${Math.min(i, 8) * 40}ms` }}
              >
                <div className="task-header">
                  <h3>{task.title}</h3>
                  {task.priority && (
                    <span className={`priority-badge ${task.priority.toLowerCase()}`}>
                      {task.priority}
                    </span>
                  )}
                </div>
                <div className="task-meta">
                  <span className={`status-badge ${task.status.toLowerCase().replace('_', '-')}`}>
                    {task.status.replace('_', ' ')}
                  </span>
                  {task.dueDate && (
                    <span className="due-date">
                      Due: {new Date(task.dueDate).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MyWork;
